import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ClearIcon from '@mui/icons-material/Clear';
import {
  Grid,
  TextField,
  InputAdornment,
  Typography,
  Box,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Collapse,
  Paper,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useApiGet } from '../../utils/httpClientTs';
import { getFinanceFundsList } from '../../endpoints/financeFunds';
import { FundListItem, FundListStatusFilter } from '../../types/financeFunds';
import ServerSideTable, {
  ServerSideTableColumn,
} from '../../Components/ServerSideTable';
import { SortOrder } from '../../types/table';
import ChapterSelector from '../../Components/ChapterSelector';
import SPDSelector from '../../Components/SPDSelector';

const hasActiveListFilters = (params: URLSearchParams): boolean =>
  Boolean(
    params.get('spd_chapter_id') ||
      params.get('local_chapter_id') ||
      params.get('status')
  );

const FundsList: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [order, setOrder] = useState<SortOrder>('asc');
  const [orderBy, setOrderBy] = useState<keyof FundListItem>('account_id');
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(25);
  const [search, setSearch] = useState<string>(
    searchParams.get('search') || ''
  );
  const [delaySearch, setDelaySearch] = useState<string>(
    searchParams.get('search') || ''
  );
  const [spdChapterId, setSpdChapterId] = useState<string>(
    searchParams.get('spd_chapter_id') || ''
  );
  const [localChapterId, setLocalChapterId] = useState<string>(
    searchParams.get('local_chapter_id') || ''
  );
  const [statusFilter, setStatusFilter] = useState<FundListStatusFilter | ''>(
    () => {
      const s = searchParams.get('status');
      return s === 'active' || s === 'inactive' ? s : '';
    }
  );
  const [showFilters, setShowFilters] = useState<boolean>(() =>
    hasActiveListFilters(searchParams)
  );

  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (search) next.set('search', search);
    else next.delete('search');
    if (spdChapterId) next.set('spd_chapter_id', spdChapterId);
    else next.delete('spd_chapter_id');
    if (localChapterId) next.set('local_chapter_id', localChapterId);
    else next.delete('local_chapter_id');
    if (statusFilter) next.set('status', statusFilter);
    else next.delete('status');
    const current = searchParams.toString();
    const target = next.toString();
    if (current !== target) setSearchParams(next, { replace: true });
  }, [
    search,
    spdChapterId,
    localChapterId,
    statusFilter,
    searchParams,
    setSearchParams,
  ]);

  const params = {
    page: page + 1,
    page_size: rowsPerPage,
    search: search || undefined,
    order_by: String(orderBy),
    order: order || undefined,
    spd_chapter_id: spdChapterId || undefined,
    local_chapter_id: localChapterId || undefined,
    status: statusFilter || undefined,
  };

  const { data, isLoading } = useApiGet(
    [
      'finance-funds',
      rowsPerPage,
      page,
      orderBy,
      order,
      search,
      spdChapterId,
      localChapterId,
      statusFilter,
    ],
    () => getFinanceFundsList(params),
    {
      enabled: true,
      retry: 1,
    }
  );

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setSearch(delaySearch);
      setPage(0);
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [delaySearch]);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const handleSort = (property: keyof FundListItem) => {
    if (property !== 'name' && property !== 'account_id') {
      return;
    }
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    setPage(0);
  };

  const handleSpdChange = (value: string | null) => {
    setSpdChapterId(value || '');
    setLocalChapterId('');
    setPage(0);
  };

  const handleChapterSelect = (chapterId: string | null) => {
    setLocalChapterId(chapterId || '');
    setPage(0);
  };

  const handleStatusChange = (event: SelectChangeEvent<string>) => {
    const v = event.target.value;
    setStatusFilter(v === 'active' || v === 'inactive' ? v : '');
    setPage(0);
  };

  const handleClearFilters = () => {
    setDelaySearch('');
    setSearch('');
    setSpdChapterId('');
    setLocalChapterId('');
    setStatusFilter('');
    setPage(0);
    setShowFilters(false);
    setSearchParams(new URLSearchParams(), { replace: true });
  };

  const columns: ServerSideTableColumn<FundListItem>[] = [
    {
      id: 'name',
      label: 'Name',
      width: '66%',
      firstCell: true,
      render: (_value, row) => (
        <Typography
          variant="body2"
          sx={{
            maxWidth: 480,
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            lineHeight: 1.35,
          }}
        >
          {row.name || '—'}
        </Typography>
      ),
    },
    {
      id: 'account_id',
      label: 'Account',
      width: '34%',
    },
  ];

  return (
    <Grid container paddingLeft={2} paddingRight={2}>
      <Grid item xs={12} padding={1}>
        <Typography variant="h4" color="primary">
          Funds
        </Typography>
      </Grid>
      {data?.can_create === true && (
        <Grid item xs={12}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              pb: 1,
            }}
          >
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => navigate('/finance/funds/new')}
              id="funds-create-button"
            >
              Create fund
            </Button>
          </Box>
        </Grid>
      )}
      <Grid item xs={12}>
        <Paper sx={{ p: 2, mb: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={8}>
              <TextField
                label="Search by name or account"
                id="funds-search-field"
                size="small"
                value={delaySearch}
                fullWidth
                onChange={(event) => setDelaySearch(event.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Button
                variant="outlined"
                onClick={() => setShowFilters(!showFilters)}
                startIcon={<FilterListIcon />}
                endIcon={showFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                sx={{ minWidth: { xs: '100%', md: 'auto' }, mr: 1 }}
              >
                Filters
              </Button>
              <Button
                variant="outlined"
                onClick={handleClearFilters}
                startIcon={<ClearIcon />}
                sx={{
                  minWidth: { xs: '100%', md: 'auto' },
                  mt: { xs: 1, md: 0 },
                }}
              >
                Clear
              </Button>
            </Grid>
          </Grid>

          <Collapse in={showFilters}>
            <Box
              sx={{
                mt: 2,
                pt: 2,
                borderTop: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <SPDSelector
                    spd={spdChapterId}
                    keyId="funds-list"
                    all={true}
                    handleSpdSelect={handleSpdChange}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <ChapterSelector
                    chapter={localChapterId}
                    spd={spdChapterId || null}
                    all={!spdChapterId}
                    keyId="funds-list"
                    label="Local chapter"
                    handleChapterSelect={handleChapterSelect}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="funds-status-filter-label">
                      Status
                    </InputLabel>
                    <Select
                      labelId="funds-status-filter-label"
                      id="funds-status-filter"
                      value={statusFilter}
                      label="Status"
                      onChange={handleStatusChange}
                    >
                      <MenuItem value="">
                        <em>All</em>
                      </MenuItem>
                      <MenuItem value="active">Active</MenuItem>
                      <MenuItem value="inactive">Inactive</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Box>
          </Collapse>
        </Paper>
      </Grid>
      <Grid item xs={12}>
        <ServerSideTable
          data={data?.data || []}
          columns={columns}
          loading={isLoading}
          page={page}
          rowsPerPage={rowsPerPage}
          totalCount={data?.pagination?.total_count || 0}
          totalPages={data?.pagination?.total_pages || 0}
          hasNext={data?.pagination?.has_next || false}
          hasPrev={data?.pagination?.has_prev || false}
          order={order}
          orderBy={String(orderBy)}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          onSort={handleSort}
          onRowClick={(row) => navigate(`/finance/funds/${row.id}`)}
          emptyMessage="No funds found."
        />
      </Grid>
    </Grid>
  );
};

export default FundsList;
